import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RoutePersistenceService from '../lib/routePersistence';

/**
 * Hook to persist and restore route when app is backgrounded/foregrounded
 */
export function useRoutePersistence() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRestoredRef = useRef(false);
  const isInitialMountRef = useRef(true);

  // Save route on navigation
  useEffect(() => {
    // Don't save on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Save current route
    RoutePersistenceService.saveRoute(location.pathname, location.state);
  }, [location.pathname, location.state]);

  // Restore route on mount ONLY (not on visibility change)
  useEffect(() => {
    if (hasRestoredRef.current) {
      return;
    }

    // Only restore on initial mount, not on visibility changes
    // On page refresh, the browser maintains the URL, so we don't need to restore
    // Route persistence is primarily for when the app is completely closed and reopened
    
    // Check if we should restore
    if (RoutePersistenceService.shouldRestoreRoute()) {
      const savedRoute = RoutePersistenceService.getSavedRoute();

      if (savedRoute && savedRoute.pathname !== location.pathname) {
        // Opening `/` is the marketing landing; don't auto-bounce to a saved `/auth` session.
        if (location.pathname === '/' && savedRoute.pathname === '/auth') {
          hasRestoredRef.current = true;
          console.log('ℹ️ Skipping restore to /auth — showing home landing');
        } else if (location.pathname === '/' || location.pathname === '/auth') {
          // Only restore if we're on the home page or auth page
          // This prevents redirecting away from the current page on refresh
          hasRestoredRef.current = true;
          console.log('🔄 Restoring saved route:', savedRoute.pathname);
          navigate(savedRoute.pathname, { state: savedRoute.state, replace: true });
        } else {
          // We're on a different page (like /workout-details/123), so don't restore
          // The browser already maintained the URL on refresh
          console.log('ℹ️ Not restoring route - already on:', location.pathname);
        }
      } else if (savedRoute && savedRoute.pathname === location.pathname) {
        // We're already on the saved route, no need to restore
        console.log('ℹ️ Already on saved route:', location.pathname);
      }
    }

    hasRestoredRef.current = true;
  }, []); // Only run once on mount, not on location changes

  // Handle visibility change (screen lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App became visible - route is already saved, no need to restore
        // The route persistence will handle restoration on next navigation
      } else {
        // App became hidden - save current route
        RoutePersistenceService.saveRoute(location.pathname, location.state);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, location.state]);

  // Handle page unload (browser close, app background)
  useEffect(() => {
    const handleBeforeUnload = () => {
      RoutePersistenceService.saveRoute(location.pathname, location.state);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, location.state]);
}

